import React, { useEffect, useState } from 'react';
import { BringzzBoxSlider, BringzzButton, BringzzOrder, BringzzText } from "@bringzz/components";
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer,
    BringzzIcon,
    BringzzOrderCard,
    BringzzBottomDrawer,
    BringzzCheck,
    BringzzMultiSelect,
    BringzzBasketProduct
} from "@bringzz/components";
import useRequest from 'hooks/useRequest';
import { useBasket } from 'context/BasketContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const ORDER_STATUS = {
    0: 'Open',
    1: 'Matching Pharmacy',
    2: 'Pharmacy Matched',
    3: 'Being Prepared',
    4: 'Prepared',
    5: 'Shopper Matched',
    6: 'Shopper Arrived at Customer',
    7: 'Shopper Identified by Customer',
    8: 'Shopper Arrived at Pharmacy',
    9: 'Shopper Identified by Pharmacy',
    10: 'Order Confirmed by Pharmacy',
    11: 'Order Confirmed by Shopper',
    12: 'Shopper Arrived with Package',
    13: 'Shopper Identified with Package',
    14: 'Order Confirmed by Shopper with Package',
    15: 'Completed',
    16: 'Canceled',
    17: 'Error'
};

const getStatusText = (status) => {
    return ORDER_STATUS[status] || 'Unknown Status';
};

const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    try {
        // Parse date in format "DD.MM.YYYY HH:mm"
        const [datePart, timePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('.');
        const [hours, minutes] = timePart.split(':');
        
        // Create date object (month is 0-based in JS Date)
        const date = new Date(year, month - 1, day, hours, minutes);
        
        // Return if invalid date
        if (isNaN(date.getTime())) {
            return 'Invalid date';
        }
        
        return date;
    } catch (e) {
        return 'Invalid date';
    }
};

let itemState = {
    checked: (
        <BringzzIcon
            icon="IconCheck"
            library="Custom"
            className="h-5 w-5 p-1 bg-magic-lilac text-light rounded-full"
            size="12"
        />
    ),
    unChecked: (
        <div></div>
    ),
};

const CompletedOrderCard = ({ order, onView, onReorder }) => {
    return (
        <div>
            <BringzzOrderCard
                orderDetails={{
                    name: order.pharmacyName || 'Unknown Pharmacy',
                    date: formatDate(order.created),
                    price: order.price ? `${(parseFloat(order.price) || 0).toFixed(2)}` : 'Price not available',
                    itemsCount: order.products?.length || 0,
                    status: getStatusText(order.deliveryStatus)
                }}
            />
            <div className="flex gap-4 mt-4">
                <BringzzButton
                    className="flex-1 bg-white border border-magic-lilac text-magic-lilac"
                    onClick={onView}
                >
                    View
                </BringzzButton>
                <BringzzButton
                    className="flex-1 bg-magic-lilac text-white"
                    onClick={onReorder}
                >
                    Reorder
                </BringzzButton>
            </div>
        </div>
    );
};

const ConsumerOrdersPage = () => {
    const { t } = useTranslation();
    const { backPress } = useNavigation();
    const { navigate } = useNavigation();
    const { sendRequest, loading } = useRequest();
    const { addToBasket } = useBasket();
    const [orders, setOrders] = useState([]);
    const [reorderDrawerOpen, setReorderDrawerOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [addingToBasket, setAddingToBasket] = useState(false);

    const handleMultiSelectChange = (selectedValues) =>
        setSelectedItems(selectedValues);

    const fetchOrders = async () => {
        await sendRequest({
            method: 'GET',
            url: `/module/order/getAllOrders`,
        }).then((response) => {
            if (response.status === 'success') {
                setOrders(response.data);
            }
        }).catch((error) => {
            console.error(error);
        });
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const getActiveOrders = () => {
        return orders.filter(order => order.deliveryStatus < 15);
    };

    const getCompletedOrders = () => {
        return orders.filter(order => order.deliveryStatus >= 15);
    };

    const handleAddToBasket = async () => {
        try {
            setAddingToBasket(true);
            const selectedProducts = selectedOrder.products.filter(product => 
                selectedItems.includes(product.name || 'Unknown Product')
            );

            // Add all selected products at once
            for (const product of selectedProducts) {
                await addToBasket({
                    integrationId: product.PharmacyProductIntegrationId || product.productIntegrationId || product.id,
                    productIntegrationId: product.PharmacyProductIntegrationId || product.productIntegrationId || product.id,
                    name: product.name || 'Unknown Product',
                    quantity: product.quantity || 1,
                    price: product.price || 0,
                    imgUrl: product.productImage || '/product.png',
                    manufacturer: product.manufacturer || '',
                    amount: product.packageQuantity && product.packaging ? 
                        `${product.packageQuantity} ${product.packaging}` : 
                        'Package details not available'
                });
            }

            toast.success('Selected items added to basket');
            setReorderDrawerOpen(false);
            setSelectedOrder(null);
            setSelectedItems([]);
            navigate('/basket');
        } catch (error) {
            toast.error('Failed to add items to basket');
            console.error(error);
        } finally {
            setAddingToBasket(false);
        }
    };

    return (
        <BringzzPageContainer
            className="bg-white pb-44"
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconChevronLeft'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: t('component.orders.title')
            }}
            authenticated={false}
        >
            {loading ? (
                <div className="flex items-center justify-center p-8">
                    <BringzzIcon
                        size="32"
                        icon="IconSpinner"
                        folder="LineIcons"
                        className="animate-spin text-magic-lilac"
                    />
                </div>
            ) : (
                <div>
                    {getActiveOrders().length === 0 && getCompletedOrders().length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-8 text-center">
                            <BringzzIcon
                                size="48"
                                icon="IconShoppingBag"
                                folder="LineIcons"
                                className="text-gray-400 mb-4"
                            />
                            <BringzzText tag="h3" className="text-gray-600 mb-2">
                                {t('component.orders.empty.title')}
                            </BringzzText>
                            <BringzzText className="text-gray-500">
                                {t('component.orders.empty.description')}
                            </BringzzText>
                        </div>
                    ) : (
                        <>
                            {getActiveOrders().length > 0 && (
                                <div className='px-6 pb-2'>
                                    <div className='flex justify-between items-center'>
                                        <BringzzText tag='h3' className={"my-4"}>
                                            {t('component.orders.active_orders')}
                                        </BringzzText>
                                    </div>
                                    <div className='space-y-4'>
                                        {getActiveOrders().map((order, index) => (
                                            <div key={order.id || index}>
                                                <BringzzOrderCard
                                                    orderDetails={{
                                                        name: order.pharmacyName || 'Unknown Pharmacy',
                                                        date: formatDate(order.created),
                                                        price: order.price ? `${(parseFloat(order.price) || 0).toFixed(2)}` : 'Price not available',
                                                        itemsCount: order.products?.length || 0,
                                                        status: getStatusText(order.deliveryStatus)
                                                    }}
                                                    mode="default"
                                                    onClick={() => navigate(`/consumer/orders/view/${order.integrationId}`)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {getCompletedOrders().length > 0 && (
                                <div className='px-6 pb-8'>
                                    <div className='flex justify-between items-center'>
                                        <BringzzText tag='h3' className={"my-4"}>
                                            {t('component.orders.past_orders')}
                                        </BringzzText>
                                    </div>
                                    <div className='space-y-4'>
                                        {getCompletedOrders().map((order, index) => (
                                            <div key={order.id || index}>
                                                <BringzzOrderCard
                                                    orderDetails={{
                                                        name: order.pharmacyName || 'Unknown Pharmacy',
                                                        date: formatDate(order.created),
                                                        price: order.price ? `${(parseFloat(order.price) || 0).toFixed(2)}` : 'Price not available',
                                                        itemsCount: order.products?.length || 0,
                                                        status: getStatusText(order.deliveryStatus)
                                                    }}
                                                    mode="completed"
                                                    onView={() => navigate(`/consumer/orders/view/${order.integrationId}`)}
                                                    onReorder={() => {
                                                        setSelectedOrder(order);
                                                        setReorderDrawerOpen(true);
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <BringzzBottomDrawer
                        isOpen={reorderDrawerOpen}
                        close={() => {
                            setReorderDrawerOpen(false);
                            setSelectedOrder(null);
                            setSelectedItems([]);
                        }}
                        title={t('component.orders.reorder.select_items')}
                    >
                        {selectedOrder && (
                            <BringzzMultiSelect
                                onChange={handleMultiSelectChange}
                                state={{ checked: itemState.checked, unChecked: itemState.unChecked }}
                                className="divide-y px-6"
                            >
                                {(selectedOrder.products || []).map((product, index) => (
                                    <BringzzCheck mode='custom' key={index} value={product.name || 'Unknown Product'}>
                                        <BringzzBasketProduct
                                            key={index + 'product'}
                                            product={{
                                                name: product.name || 'Unknown Product',
                                                quantity: product.quantity || 1,
                                                price: product.price || 0,
                                                amount: product.packageQuantity && product.packaging ? 
                                                    `${product.packageQuantity} ${product.packaging}` : 
                                                    'Package details not available',
                                                imgUrl: product.productImage || '/product.png',
                                                manufacturer: product.manufacturer || ''
                                            }}
                                            mode="select"
                                        />
                                    </BringzzCheck>
                                ))}
                            </BringzzMultiSelect>
                        )}
                        <div className='w-full px-6 py-2'>
                            <BringzzButton 
                                disabled={selectedItems.length === 0 || addingToBasket} 
                                size='lg' 
                                className='w-full bg-magic-lilac flex justify-center items-center' 
                                onClick={handleAddToBasket}
                            >
                                {addingToBasket ? (
                                    <>
                                        <BringzzIcon
                                            size="20"
                                            icon="IconSpinner"
                                            folder="LineIcons"
                                            className="animate-spin mr-2"
                                        />
                                        {t('component.orders.reorder.adding_to_cart')}
                                    </>
                                ) : (
                                    t('component.orders.reorder.add_to_cart')
                                )}
                            </BringzzButton>
                        </div>
                    </BringzzBottomDrawer>
                </div>
            )}
        </BringzzPageContainer>
    );
};

export default ConsumerOrdersPage;
